import { useId, useRef } from "react";
import { cn } from "@/lib/utils";

export interface TabBarItem<TKey extends string = string> {
  key: TKey;
  label: React.ReactNode;
  /** Optional count badge rendered after the label. Useful for "Claims 14",
   *  "Endorsements 3" etc. Hidden when undefined or when the count is 0
   *  (a zero-count badge adds visual noise without conveying anything). */
  count?: number;
}

interface TabBarProps<TKey extends string> {
  /** Visible tabs in display order. The active tab does NOT need to be the
   *  first entry; arrow-key wraparound is computed from the current key. */
  tabs: ReadonlyArray<TabBarItem<TKey>>;
  activeKey: TKey;
  onChange: (next: TKey) => void;
  /** ARIA accessible name for the tablist. Screen readers announce this so
   *  keyboard users know which group of tabs they're navigating. */
  ariaLabel: string;
  className?: string;
}

/**
 * Accessible tab strip. Behaviour:
 *  - container is `role="tablist"`
 *  - each button is `role="tab"`, with `aria-selected` and roving `tabindex`
 *    (active tab = 0, others = -1) so Tab moves to/from the strip in one
 *    keystroke and the user navigates inside it with arrow keys
 *  - Left/Right arrows move between tabs and wrap; Home/End jump to the
 *    first/last; activation is automatic (selected on focus, matching the
 *    "automatic activation" pattern in the WAI-ARIA Authoring Practices).
 *
 * Caller renders the panels themselves and switches on `activeKey`. If the
 * caller wants associated `role="tabpanel"` regions, pair each panel with the
 * id this component exposes via the data-tab-id attribute (or pass a stable
 * key and derive ids on both sides).
 */
export function TabBar<TKey extends string>({
  tabs,
  activeKey,
  onChange,
  ariaLabel,
  className,
}: TabBarProps<TKey>) {
  const baseId = useId();
  const refs = useRef(new Map<TKey, HTMLButtonElement>());

  const moveFocus = (nextKey: TKey) => {
    onChange(nextKey);
    // Defer the focus move so the re-render with the updated tabIndex lands
    // first; otherwise screen readers may skip the announcement.
    requestAnimationFrame(() => {
      refs.current.get(nextKey)?.focus();
    });
  };

  const handleKey = (e: React.KeyboardEvent) => {
    const idx = tabs.findIndex((t) => t.key === activeKey);
    if (idx < 0) return;
    let nextIdx: number | null = null;
    if (e.key === "ArrowRight") nextIdx = (idx + 1) % tabs.length;
    else if (e.key === "ArrowLeft") nextIdx = (idx - 1 + tabs.length) % tabs.length;
    else if (e.key === "Home") nextIdx = 0;
    else if (e.key === "End") nextIdx = tabs.length - 1;
    if (nextIdx === null) return;
    e.preventDefault();
    const next = tabs[nextIdx];
    if (next) moveFocus(next.key);
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn("-mb-px flex flex-wrap gap-1 border-b", className)}
      onKeyDown={handleKey}
    >
      {tabs.map((tab) => {
        const isActive = tab.key === activeKey;
        return (
          <button
            key={tab.key}
            ref={(el) => {
              if (el) refs.current.set(tab.key, el);
              else refs.current.delete(tab.key);
            }}
            type="button"
            role="tab"
            id={`${baseId}-tab-${tab.key}`}
            aria-selected={isActive}
            aria-controls={`${baseId}-panel-${tab.key}`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(tab.key)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-t-md px-3 py-2 font-medium text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
              isActive
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
            )}
          >
            {tab.label}
            {typeof tab.count === "number" && tab.count > 0 && (
              <span
                className={cn(
                  "inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 py-px text-xs leading-none tabular-nums",
                  isActive
                    ? "bg-primary-foreground/20 text-primary-foreground"
                    : "bg-muted text-muted-foreground",
                )}
              >
                {tab.count.toLocaleString()}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
